import { useEffect, useState } from "react";
import { Scale, Info, ChevronDown, ChevronUp } from "lucide-react";
import { theme, card, inputStyle, btnPrimary } from "../theme";
import { RulerProgress } from "./Drawing";
import { api } from "../api/client";
import { CountryPicker } from "../pages/OrgProfile";

// ── Types (mirror backend/src/lib/dataProtectionThresholds.ts) ─────────────

export type ApplicabilityStatus = "required" | "conditional" | "exempt" | "unknown";

export interface Determination {
  regulation: string;
  controlId: string | null;
  status: ApplicabilityStatus;
  headline: string;
  detail: string;
  basis: string;
  missingInputs: string[];
  recommended?: boolean;
  /** The provision is not in force yet (backend lib/lawCommencement.ts); `from` null = no date appointed. */
  notInForce?: { from: string | null };
}

export interface DpProfile {
  turnoverAmount?: number | null;
  turnoverCurrency?: string | null;
  dataSubjectBand?: string | null;
  specialCategoryScale?: string | null;
  criminalOffenceData?: boolean | null;
  childrenData?: boolean | null;
  monitoringCoreActivity?: boolean | null;
  publicAuthority?: boolean | null;
  nonProfit?: boolean | null;
  healthServiceProvider?: boolean | null;
  sellsPersonalData?: boolean | null;
  sellingRevenueMajority?: boolean | null;
  occasionalProcessingOnly?: boolean | null;
  establishmentCountries?: string[] | null;
  essentialServiceOperator?: boolean | null;
  // Designation-threshold figures (backend/src/lib/oesRegimes.ts).
  essentialServiceCustomers?: number | null;
  essentialServiceVolumeAnnual?: number | null;
  essentialServiceVolumeUnit?: string | null;
  populationServed?: number | null;
  regulatoryLicences?: string[] | null;
  // Status inputs asked only where a law that reads them is in scope (SCOPED_PROFILE_KEYS).
  secRegisteredIssuer?: boolean | null;
  connectedProductSupplier?: boolean | null;
  productManufacturer?: boolean | null;
  uaeFinancialFreeZone?: string | null;
  ictProviderToFinancialEntities?: boolean | null;
  // Territorial scope (backend lib/jurisdictions.ts TerritorialScope).
  targetedCountries?: string[] | null;
  hostingOnlyCountries?: string[] | null;
  // GDPR-family refinements and the EU enterprise size.
  processingRiskToIndividuals?: boolean | null;
  criminalOffenceScale?: string | null;
  sensitiveDataCoreActivity?: boolean | null;
  profilingSignificantEffects?: boolean | null;
  publicAreaMonitoring?: boolean | null;
  onlineServiceForChildren?: boolean | null;
  balanceSheetAmount?: number | null;
  partOfGroup?: boolean | null;
  groupHeadcount?: number | null;
  groupTurnoverAmount?: number | null;
  groupBalanceSheetAmount?: number | null;
  // Single-law status inputs.
  nydfsLicensee?: boolean | null;
  dataBroker?: boolean | null;
  federalInformationSystems?: boolean | null;
  auPrivacyActCarveOut?: boolean | null;
}

export interface ThresholdsPayload {
  lastReviewed: string;
  profile: DpProfile;
  facts: {
    headcountLabel: string;
    turnover: { amount: number; currency: string } | null;
    dataSubjectBand: string | null;
    jerseyTier: string | null;
  };
  completeness: { answered: number; total: number; missing: string[] };
  /** Designation-threshold figures this organisation is actually asked for; empty for most. */
  oesInputs?: string[];
  /**
   * The licence keys worth offering: those of the laws in this organisation's own jurisdictions.
   * The server decides, so a UK-only organisation is never shown a Jersey telecoms licence.
   * Absent (an older response) falls back to the whole list rather than to none.
   */
  oesLicences?: string[];
  /** Status questions a law in this organisation's scope reads (SOX, CRA / PSTI, DIFC / ADGM); empty for most. */
  scopedInputs?: string[];
  regulationsWithoutRules: string[];
  /** The edition carries no data protection (the Jersey edition): only the designation questions are asked. */
  designationOnly?: boolean;
  /** Minimum length of the reason "mark not applicable" asks for. */
  notApplicableReasonMin?: number;
}

const INPUT_LABELS: Record<string, string> = {
  headcount:                "Headcount",
  turnoverAmount:           "Annual turnover",
  turnoverCurrency:         "Turnover currency",
  dataSubjectBand:          "Number of individuals whose data you hold",
  specialCategoryScale:     "Special category / sensitive data",
  criminalOffenceData:      "Criminal offence data",
  childrenData:             "Children's data",
  monitoringCoreActivity:   "Systematic monitoring as a core activity",
  publicAuthority:          "Public authority or body",
  nonProfit:                "Non-profit / charity",
  healthServiceProvider:    "Health service provider",
  sellsPersonalData:        "Sells, shares or trades personal data",
  sellingRevenueMajority:   "Half or more of revenue from selling personal data",
  occasionalProcessingOnly: "Processing is only occasional",
  establishmentCountries:   "Countries of legal establishment",
  essentialServiceOperator: "Designated operator of essential services",
  essentialServiceCustomers:    "Customers served by your essential service",
  essentialServiceVolumeAnnual: "Annual volume of your essential service",
  essentialServiceVolumeUnit:   "Unit the annual volume is counted in",
  populationServed:             "Population your service covers",
  regulatoryLicences:           "Licences and designations you hold",
  secRegisteredIssuer:          "Listed or SEC-registered company (US)",
  connectedProductSupplier:     "Makes, imports or distributes connectable products or software",
  productManufacturer:          "Manufacturer of products with digital elements (not only importer or distributor)",
  uaeFinancialFreeZone:         "Established in a UAE financial free zone (DIFC / ADGM)",
  ictProviderToFinancialEntities: "Provides ICT services to EU financial entities",
  targetedCountries:            "Countries whose residents you target or monitor without an establishment",
  hostingOnlyCountries:         "Operating Locations where you only host or process data",
  processingRiskToIndividuals:  "Processing likely to result in a risk to individuals",
  criminalOffenceScale:         "Scale of criminal offence data",
  sensitiveDataCoreActivity:    "Sensitive data processing is a core activity",
  profilingSignificantEffects:  "Profiling or automated decisions with significant effects",
  publicAreaMonitoring:         "Large-scale monitoring of publicly accessible areas",
  onlineServiceForChildren:     "Online service likely to be accessed by children (UK)",
  balanceSheetAmount:           "Annual balance-sheet total",
  partOfGroup:                  "Part of a group (25% or more owned by, or owning, another enterprise)",
  groupHeadcount:               "Group headcount",
  groupTurnoverAmount:          "Group turnover",
  groupBalanceSheetAmount:      "Group balance-sheet total",
  nydfsLicensee:                "Licensed, registered or chartered by the New York DFS",
  dataBroker:                   "Sells data about people you have no direct relationship with",
  federalInformationSystems:    "US federal agency, or runs an information system for one",
  auPrivacyActCarveOut:         "Australia: covered whatever your turnover (s.6D exceptions)",
};

// Mirrors UAE_FINANCIAL_FREE_ZONES in backend/src/lib/dataProtectionThresholds.ts.
const UAE_FREE_ZONES: { value: string; label: string }[] = [
  { value: "difc", label: "Dubai International Financial Centre (DIFC)" },
  { value: "adgm", label: "Abu Dhabi Global Market (ADGM)" },
  { value: "both", label: "Both DIFC and ADGM" },
  { value: "none", label: "Neither — UAE mainland or another free zone" },
];

// Mirrors ESSENTIAL_SERVICE_VOLUME_UNITS / REGULATORY_LICENCES in backend/src/lib/oesRegimes.ts.
// Display only: which threshold a figure meets is decided server-side.
const VOLUME_UNITS: { value: string; label: string }[] = [
  { value: "litres_fuel", label: "Litres of fuel a year" },
  { value: "cubic_metres_water", label: "Cubic metres of water a year" },
  { value: "mwh_electricity", label: "MWh of electricity a year" },
  { value: "passengers", label: "Passengers a year" },
  { value: "tonnes_freight", label: "Tonnes of freight a year" },
  { value: "other", label: "Other unit" },
];

/** The "none of these" key, as backend/src/lib/oesRegimes.ts spells it. */
const LICENCE_NONE = "none";

const LICENCES: { value: string; label: string }[] = [
  { value: "je_telecoms_class_ii", label: "Jersey telecoms licence — Class II" },
  { value: "je_telecoms_class_iii", label: "Jersey telecoms licence — Class III" },
  { value: "je_banking_bbjl", label: "Registered under the Banking Business (Jersey) Law 1991" },
  { value: "je_designated_by_minister", label: "Designated by the Minister (Cyber Security (Jersey) Law)" },
  { value: "uk_nis_designated", label: "Designated an operator of essential services (UK NIS)" },
  { value: "nis2_designated", label: "Registered as an essential or important entity (NIS2)" },
  { value: "ie_nis_designated", label: "Designated an operator of essential services (Irish NIS Regulations)" },
  { value: "soci_designated", label: "Responsible entity for a critical infrastructure asset (SOCI Act)" },
  { value: "sg_cii_designated", label: "Owner of critical information infrastructure (Singapore)" },
  { value: "es_nis_designated", label: "Designated operator of essential services, or digital service provider (Spain, RDL 12/2018)" },
  { value: "no_digital_security_designated", label: "Provider of a socially important service, or digital service provider (Norway)" },
  { value: "ch_isg_critical_infrastructure", label: "Critical infrastructure that must report cyberattacks to the BACS (Switzerland)" },
  { value: "hk_cio_designated", label: "Designated critical infrastructure operator (Hong Kong, Cap. 653)" },
  { value: "my_ncii_designated", label: "Designated national critical information infrastructure entity (Malaysia)" },
  { value: "tw_csma_designated", label: "Designated specific non-government agency (Taiwan)" },
  { value: "cn_ciio", label: "Identified as a critical information infrastructure operator (China)" },
  // The explicit "none" answer. It is stored like any other key because an empty licence list
  // means "not recorded" server-side (unknown, which never exempts), so without it the licence
  // limb of every Schedule stays unanswered for ever. Never offered on its own.
  { value: LICENCE_NONE, label: "None of these" },
];

export function inputLabel(key: string): string {
  return INPUT_LABELS[key] ?? key;
}

const CURRENCIES = [
  "GBP", "EUR", "USD", "AUD", "CAD", "CHF", "BRL", "TRY", "INR", "CNY", "JPY", "KRW", "SGD", "MYR",
  "THB", "PHP", "VND", "ZAR", "AED", "SAR", "QAR", "OMR", "ILS", "MXN", "NZD", "HKD", "TWD", "NOK",
  "SEK", "DKK", "ISK", "PLN", "CZK", "HUF", "RON", "BGN",
];

const DATA_SUBJECT_BANDS: { value: string; label: string }[] = [
  { value: "lt_1k", label: "Under 1,000" },
  { value: "1k_10k", label: "1,000 – 10,000" },
  { value: "10k_100k", label: "10,000 – 100,000" },
  { value: "100k_1m", label: "100,000 – 1 million" },
  { value: "1m_10m", label: "1 – 10 million" },
  { value: "gte_10m", label: "10 million or more" },
];

const SPECIAL_CATEGORY_SCALES: { value: string; label: string }[] = [
  { value: "none", label: "None" },
  { value: "limited", label: "Limited (e.g. occupational health, DBS checks, dietary needs)" },
  { value: "large", label: "Large scale / core activity (e.g. patient, biometric or welfare records)" },
];

// ── Status presentation ───────────────────────────────────────────────────

export const STATUS_STYLE: Record<ApplicabilityStatus, { label: string; bg: string; color: string; border: string }> = {
  required:    { label: "Mandatory",      bg: "var(--c-accentBg)",  color: "var(--c-accentText)",  border: "var(--c-accent)" },
  conditional: { label: "Check",          bg: "var(--c-warningBg)", color: "var(--c-warning)",     border: "var(--c-warning)" },
  exempt:      { label: "Exempt",         bg: "var(--c-successBg)", color: "var(--c-successText)", border: "var(--c-success)" },
  unknown:     { label: "Profile needed", bg: "var(--c-panelAlt)",  color: "var(--c-muted)",       border: "var(--c-borderStrong)" },
};

/** Small pill shown on the obligation summary row. */
export function ApplicabilityBadge({ determination, applicable }: { determination: Determination | null; applicable: boolean }) {
  // An explicit override that disagrees with the engine is shown as what it is.
  if (!applicable) {
    if (determination?.notInForce) {
      const n = STATUS_STYLE.unknown;
      return <Pill label="Not yet in force" bg={n.bg} color={n.color} border={n.border} />;
    }
    const s = STATUS_STYLE.exempt;
    return <Pill label={determination?.status === "exempt" ? "Exempt" : "Not applicable"} bg={s.bg} color={s.color} border={s.border} />;
  }
  if (!determination) return null;
  if (determination.status === "exempt") {
    const s = STATUS_STYLE.required;
    return <Pill label="Applicable (override)" bg={s.bg} color={s.color} border={s.border} />;
  }
  const s = STATUS_STYLE[determination.status];
  return <Pill label={s.label} bg={s.bg} color={s.color} border={s.border} />;
}

function Pill({ label, bg, color, border }: { label: string; bg: string; color: string; border: string }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 3, background: bg, color, border: `1px solid ${border}`, whiteSpace: "nowrap", flexShrink: 0 }}>
      {label}
    </span>
  );
}

/** The determination panel inside an expanded obligation, with the applicability override. */
export function DeterminationCallout({
  determination, override, applicable, saving, onOverride, canDecide = true, reason = null, reasonMin = 20, conflict = false,
}: {
  determination: Determination | null;
  override: boolean | null;          // stored SoaEntry.applicable
  applicable: boolean;               // effective
  saving: boolean;
  /** Resolves to an error sentence when the server refuses the decision, or null. */
  onOverride: (value: boolean | null, reason?: string) => Promise<string | null> | void;
  /** legal:write — deciding an obligation does not apply. */
  canDecide?: boolean;
  /** The reason recorded with a not-applicable decision (SoaEntry.justification). */
  reason?: string | null;
  /** Minimum length of that reason (GET /legal/soa thresholds.notApplicableReasonMin). */
  reasonMin?: number;
  /** Marked not applicable although the engine says it is required: the incident duty stays. */
  conflict?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState("");
  const [refusal, setRefusal] = useState("");
  async function decide(value: boolean | null, why?: string) {
    setRefusal("");
    const result = await onOverride(value, why);
    if (typeof result === "string" && result) setRefusal(result);
    else { setAsking(false); setDraft(""); }
  }
  // A duty with no threshold test still has an applicability decision: the people entitled to make it
  // may take it off the list (which also switches off its incident notifications).
  const status: ApplicabilityStatus = determination?.status ?? "required";
  // A provision not yet in force reads as what it is, never as an exemption the organisation earned.
  const s = determination?.notInForce ? { ...STATUS_STYLE.unknown, label: "Not yet in force" } : STATUS_STYLE[status];
  const overridden = override !== null;
  return (
    <div style={{ margin: "0 0 10px", padding: "9px 12px", background: s.bg, border: `1px solid ${s.border}`, borderRadius: 3, display: "flex", gap: 8, alignItems: "flex-start" }}>
      <Scale size={13} style={{ flexShrink: 0, marginTop: 2, color: s.color }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: s.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
          {determination ? `Threshold check — ${s.label}` : "Applicability"}
          {overridden && <span style={{ marginLeft: 6, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>· overridden: {applicable ? "applicable" : "not applicable"}</span>}
        </div>
        {!determination && override === null && (
          <p style={{ margin: 0, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
            The platform has no threshold test for this duty, so it counts as applying to you. If it does not, mark it not applicable: it then leaves your list and your score.
          </p>
        )}
        {determination && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 3 }}>{determination.headline}</div>
            <p style={{ margin: 0, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>{determination.detail}</p>
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>Basis: {determination.basis}</div>
            {determination.missingInputs.length > 0 && (
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>
                To resolve, complete: {determination.missingInputs.map(inputLabel).join(", ")} in the data protection profile above.
              </div>
            )}
            {determination.status === "unknown" && determination.missingInputs.length === 0 && (
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>
                Every question this test reads is answered, but the answers do not settle it. Decide below whether it applies to you.
              </div>
            )}
          </>
        )}
        {override === false && reason && (
          <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6 }}>Recorded reason: {reason}</div>
        )}
        {conflict && (
          <div role="note" style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 6, lineHeight: 1.5 }}>
            The threshold check says this duty applies to you. Your decision is recorded, but an incident notification duty attaches by law once you are in scope, so incidents will still list this regulator. Review the decision, or correct the profile answers it rests on.
          </div>
        )}
        {!canDecide && (
          <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 8 }}>
            Whether this applies is decided by someone holding the legal applicability permission (Legal Counsel, an owner or a compliance manager).
          </div>
        )}
        {canDecide && asking && (
          <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 4 }}>
              Why does this obligation not apply to you? (at least {reasonMin} characters, kept with the decision)
            </label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontSize: 12 }}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
              <button disabled={saving || draft.trim().length < reasonMin} onClick={() => { void decide(false, draft.trim()); }} style={smallBtn}>
                Record as not applicable
              </button>
              <button disabled={saving} onClick={() => { setAsking(false); setDraft(""); setRefusal(""); }} style={{ ...smallBtn, border: "none" }}>
                Cancel
              </button>
              <span style={{ fontSize: 11, color: theme.color.faint }}>{draft.trim().length}/{reasonMin}</span>
            </div>
          </div>
        )}
        {canDecide && !asking && <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          {applicable ? (
            <button
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); setAsking(true); }}
              style={smallBtn}
            >
              Mark not applicable
            </button>
          ) : (
            <button
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); void decide(true); }}
              style={smallBtn}
            >
              Treat as applicable anyway
            </button>
          )}
          {overridden && (
            <button
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); void decide(null); }}
              style={{ ...smallBtn, textDecoration: "underline", border: "none", background: "transparent" }}
            >
              Follow threshold rules
            </button>
          )}
        </div>}
        {refusal && <div role="alert" style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 6 }}>{refusal}</div>}
      </div>
    </div>
  );
}

const smallBtn: React.CSSProperties = {
  background: "transparent", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3,
  padding: "3px 9px", fontSize: 11, color: theme.color.muted, cursor: "pointer",
};

/** Regulation-level facts: fees, registrations, representatives, "no threshold" notes. */
export function RegulationFacts({ facts }: { facts: Determination[] | undefined }) {
  if (!facts || facts.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
      {facts.map((d, i) => {
        const s = STATUS_STYLE[d.status];
        return (
          <FactRow key={i} determination={d} color={s.color} border={s.border} bg={s.bg} label={s.label} />
        );
      })}
    </div>
  );
}

function FactRow({ determination: d, color, border, bg, label }: { determination: Determination; color: string; border: string; bg: string; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${theme.color.border}`, borderLeft: `3px solid ${border}`, borderRadius: 3, background: theme.color.panel }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", cursor: "pointer" }}>
        <Info size={13} style={{ color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, flex: 1 }}>{d.headline}</span>
        {d.status !== "required" && <Pill label={label} bg={bg} color={color} border={border} />}
        {open ? <ChevronUp size={13} style={{ color: theme.color.faint }} /> : <ChevronDown size={13} style={{ color: theme.color.faint }} />}
      </div>
      {open && (
        <div style={{ padding: "0 12px 10px 33px" }}>
          <p style={{ margin: 0, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>{d.detail}</p>
          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>Basis: {d.basis}</div>
          {d.missingInputs.length > 0 && (
            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>
              To resolve, complete: {d.missingInputs.map(inputLabel).join(", ")}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Profile card ──────────────────────────────────────────────────────────

function TriToggle({ value, onChange, disabled }: { value: boolean | null; onChange: (v: boolean | null) => void; disabled?: boolean }) {
  const opts: { v: boolean | null; label: string }[] = [{ v: true, label: "Yes" }, { v: false, label: "No" }, { v: null, label: "Not sure" }];
  return (
    <div style={{ display: "inline-flex", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, overflow: "hidden" }}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.v)}
            style={{
              padding: "5px 11px", fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none",
              background: active ? theme.color.accent : theme.color.panelAlt,
              color: active ? "#fff" : theme.color.muted,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Question({ label, help, children, wide }: { label: string; help?: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div style={{ gridColumn: wide ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{label}</div>
      {help && <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 6, lineHeight: 1.5 }}>{help}</div>}
      {children}
    </div>
  );
}

const selectStyle: React.CSSProperties = { ...inputStyle, width: "100%", boxSizing: "border-box" };

/**
 * The threshold inputs. Headcount and sector come from My Organisation; everything else is
 * stored on the org's data protection profile and saved through /legal/dp-profile.
 */
export function DpThresholdProfileCard({
  thresholds, onSaved, canEdit = true,
}: {
  thresholds: ThresholdsPayload | null;
  onSaved: () => Promise<void> | void;
  /** legal:write or privacy:write — what PUT /legal/dp-profile requires. Without it the answers are shown read-only. */
  canEdit?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<DpProfile>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // Countries where a site is flagged as a legal establishment stay declared (the server unions them in).
  const [siteEstablishments, setSiteEstablishments] = useState<string[]>([]);

  useEffect(() => {
    if (thresholds) setForm(thresholds.profile ?? {});
  }, [thresholds]);

  useEffect(() => {
    api.get<{ sites: Array<{ country: string; isLegalEstablishment: boolean }> }>("/sites")
      .then((d) => setSiteEstablishments(Array.from(new Set(d.sites.filter((x) => x.isLegalEstablishment).map((x) => x.country)))))
      .catch(() => {});
  }, []);

  // Nothing to answer until the org has regulations (the page shows its empty state instead).
  if (!thresholds) return null;

  const { completeness } = thresholds;
  // Designation only: the count is of the designation questions, not the data protection ones.
  const designationOnly = thresholds.designationOnly === true;
  const designationKeys = ["essentialServiceOperator", ...(thresholds.oesInputs ?? [])] as Array<keyof DpProfile>;
  const shownCompleteness = designationOnly
    ? { answered: designationKeys.filter((k) => { const v = thresholds.profile?.[k]; return v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0); }).length, total: designationKeys.length }
    : completeness;
  const pct = shownCompleteness.total > 0 ? Math.round((shownCompleteness.answered / shownCompleteness.total) * 100) : 0;
  const barColor = pct === 100 ? theme.color.success : pct >= 50 ? theme.color.accent : theme.color.warning;

  // Which designation-threshold figures to show. The server decides — it knows which regimes are
  // in scope and which of their thresholds this organisation's sector could meet — so a figure
  // already answered keeps its field even once it is no longer "missing".
  const oesInputs = thresholds.oesInputs ?? [];
  const asks = (key: string) => oesInputs.includes(key);
  const asksOesFigures = oesInputs.length > 0;
  // Status questions (SEC registration, connectable products, UAE free zone): the server lists the
  // ones a law in scope reads. An answer already on file keeps its field.
  const scopedInputs = thresholds.scopedInputs ?? [];
  const asksScoped = (key: keyof DpProfile) => scopedInputs.includes(key) || (form[key] !== null && form[key] !== undefined);

  // Only the licences of the jurisdictions this organisation operates in, plus "None of these".
  // An older response without the field shows the full list, which is what it used to do.
  const inScopeLicences = thresholds.oesLicences;
  const licenceOptions = inScopeLicences
    ? LICENCES.filter((l) => l.value === LICENCE_NONE || inScopeLicences.includes(l.value))
    : LICENCES;

  function set<K extends keyof DpProfile>(key: K, value: DpProfile[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setError("");
  }

  async function save() {
    if (typeof form.turnoverAmount === "number" && form.turnoverAmount < 0) {
      setError("Annual turnover cannot be negative.");
      return;
    }
    setSaving(true); setError(""); setSaved(false);
    try {
      const body: DpProfile = { ...form };
      if (body.turnoverAmount === null || body.turnoverAmount === undefined || Number.isNaN(body.turnoverAmount)) {
        body.turnoverAmount = null;
      }
      if (body.turnoverAmount !== null && !body.turnoverCurrency) body.turnoverCurrency = "GBP";
      if (body.sellsPersonalData !== true) body.sellingRevenueMajority = body.sellsPersonalData === false ? false : null;
      await api.put("/legal/dp-profile", body);
      setSaved(true);
      await onSaved();
    } catch (e) {
      // The server's own reason (a field it refused, or a permission), never a generic "try again".
      setError(e instanceof Error && e.message ? e.message : "Could not save the profile. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const turnoverText = form.turnoverAmount === null || form.turnoverAmount === undefined ? "" : String(form.turnoverAmount);

  return (
    <div style={{ ...card, padding: 0, marginBottom: 20 }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}>
        <Scale size={16} style={{ color: theme.color.accentText, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{designationOnly ? "Essential services designation" : "Data protection thresholds"}</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
            {designationOnly
              ? "Whether a cyber-security law where you operate designates you as an operator of essential services. Answer these and the obligations below follow."
              : "Each law switches obligations on or off by size, turnover, data volumes and the nature of your processing. Answer these once and the obligations below are tuned to you."}
            {" "}Headcount ({thresholds.facts.headcountLabel}) and sector come from My Organisation.
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: barColor }}>{shownCompleteness.answered}/{shownCompleteness.total}</div>
          <div style={{ fontSize: 10, color: theme.color.faint }}>answered</div>
        </div>
        {open ? <ChevronUp size={15} style={{ color: theme.color.faint }} /> : <ChevronDown size={15} style={{ color: theme.color.faint }} />}
      </div>
      <RulerProgress value={pct} fill={barColor} label="Threshold questions answered" height={10} style={{ margin: "0 16px 8px" }} />

      {open && (
        <div style={{ padding: "16px", borderTop: `1px solid ${theme.color.border}` }}>
          {!canEdit && (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12 }}>
              Read only: these answers decide which laws apply, so changing them needs the legal applicability or privacy permission (Legal Counsel, the Data Protection Officer, an owner or a compliance manager).
            </div>
          )}
          <fieldset disabled={!canEdit} style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 18px" }}>
            {!designationOnly && (<>
            <Question label="Annual turnover" help="Gross annual revenue. Sets the ICO fee tier, the CCPA revenue test, the Australian small-business exemption, Brazilian small-agent status and the Turkish VERBİS test.">
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  type="number" min={0} step={1000} value={turnoverText} placeholder="e.g. 2500000"
                  onChange={(e) => set("turnoverAmount", e.target.value === "" ? null : Number(e.target.value))}
                  style={{ ...inputStyle, flex: 1, boxSizing: "border-box" }}
                />
                <select value={form.turnoverCurrency ?? "GBP"} onChange={(e) => set("turnoverCurrency", e.target.value)} style={{ ...inputStyle, width: 90 }}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </Question>

            <Question label="Individuals whose personal data you hold" help="Rough total across customers, users, staff and contacts. Drives the Chinese transfer mechanism, Malaysian and Philippine DPO and registration duties, CCPA volume tests and India's Significant Data Fiduciary screening.">
              <select value={form.dataSubjectBand ?? ""} onChange={(e) => set("dataSubjectBand", e.target.value || null)} style={selectStyle}>
                <option value="">— not sure —</option>
                {DATA_SUBJECT_BANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </Question>

            <Question label="Special category / sensitive data" help="Health, biometric, genetic, racial or ethnic origin, political opinions, religious beliefs, trade-union membership, sex life or orientation. Large-scale processing as a core activity triggers DPO and DPIA duties." wide>
              <select value={form.specialCategoryScale ?? ""} onChange={(e) => set("specialCategoryScale", e.target.value || null)} style={selectStyle}>
                <option value="">— not sure —</option>
                {SPECIAL_CATEGORY_SCALES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </Question>

            <Question label="Criminal offence data" help="Convictions, offences, DBS or police checks.">
              <TriToggle value={form.criminalOffenceData ?? null} onChange={(v) => set("criminalOffenceData", v)} />
            </Question>
            <Question label="Children's data" help="You process personal data of under-18s. One indicator of risk for DPIA screening; not on its own a reason a DPIA is mandatory.">
              <TriToggle value={form.childrenData ?? null} onChange={(v) => set("childrenData", v)} />
            </Question>
            <Question label="Systematic monitoring as a core activity" help="Regular, large-scale tracking, profiling, behavioural analytics or CCTV networks as part of what you do — not incidental staff IT logging.">
              <TriToggle value={form.monitoringCoreActivity ?? null} onChange={(v) => set("monitoringCoreActivity", v)} />
            </Question>
            <Question label="Processing is only occasional" help="Only true if you keep no routine payroll, HR, customer or marketing records. Almost every employer should answer No.">
              <TriToggle value={form.occasionalProcessingOnly ?? null} onChange={(v) => set("occasionalProcessingOnly", v)} />
            </Question>
            <Question label="Public authority or body" help="Government, statutory or public-sector body, or a contracted service provider exercising public functions.">
              <TriToggle value={form.publicAuthority ?? null} onChange={(v) => set("publicAuthority", v)} />
            </Question>
            <Question label="Non-profit / charity" help="Registered charity or not-for-profit association. Affects fee tiers, CCPA scope and Brazilian small-agent status.">
              <TriToggle value={form.nonProfit ?? null} onChange={(v) => set("nonProfit", v)} />
            </Question>
            <Question label="Health service provider" help="You provide a health service or hold patient records (removes the Australian small-business exemption; HIPAA covered entity).">
              <TriToggle value={form.healthServiceProvider ?? null} onChange={(v) => set("healthServiceProvider", v)} />
            </Question>
            <Question label="Sells, shares or trades personal data" help="Selling, sharing for cross-context advertising, or supplying personal data to others as a business.">
              <TriToggle value={form.sellsPersonalData ?? null} onChange={(v) => set("sellsPersonalData", v)} />
            </Question>
            {form.sellsPersonalData === true && (
              <Question label="Half or more of revenue from selling personal data" help="CCPA: 50% or more of annual revenue from selling or sharing consumers' personal information.">
                <TriToggle value={form.sellingRevenueMajority ?? null} onChange={(v) => set("sellingRevenueMajority", v)} />
              </Question>
            )}
            </>)}
            <Question label="Designated operator of essential services" help="Yes if you hold a designation under a cyber-security law where you operate — an OES under the NIS Regulations or the Cyber Security (Jersey) Law 2026, or an essential / important entity under NIS2. Designation is per law: where more than one of these laws applies to you, this answer settles none of them on its own — record which law designates you under &quot;Licences and designations you hold&quot;. A &quot;No&quot; is a no for each." wide>
              <TriToggle value={form.essentialServiceOperator ?? null} onChange={(v) => set("essentialServiceOperator", v)} />
            </Question>
            {!designationOnly && (<>
            {asksScoped("secRegisteredIssuer") && (
              <Question label="Listed or SEC-registered company (US)" help="Securities registered with the SEC or listed on a US exchange. Decides whether the Sarbanes-Oxley duties (s.302 certification, s.404 internal control, s.301 audit committee) apply to you.">
                <TriToggle value={form.secRegisteredIssuer ?? null} onChange={(v) => set("secRegisteredIssuer", v)} />
              </Question>
            )}
            {asksScoped("connectedProductSupplier") && (
              <Question label="Make, import or distribute connectable products or software" help="Hardware that connects to a network or the internet, or software sold as a product. Decides whether the EU Cyber Resilience Act and the UK PSTI product-security regime apply to you. Pure SaaS is outside both.">
                <TriToggle value={form.connectedProductSupplier ?? null} onChange={(v) => set("connectedProductSupplier", v)} />
              </Question>
            )}
            {(asksScoped("productManufacturer") || form.connectedProductSupplier === true) && (
              <Question label="Manufacture those products (not only import or distribute them)" help="You design or make a product with digital elements, or have it made, and sell it under your own name or trade mark. The Cyber Resilience Act's manufacturer obligations, including reporting actively exploited vulnerabilities and severe incidents to the CSIRT and ENISA within 24 hours (Art. 14, from 11 September 2026), fall on the manufacturer only.">
                <TriToggle value={form.productManufacturer ?? null} onChange={(v) => set("productManufacturer", v)} />
              </Question>
            )}
            {asksScoped("ictProviderToFinancialEntities") && (
              <Question label="Provide ICT services to EU financial entities" help="Cloud, software, data, hosting or managed services that a bank, insurer, investment firm, payment or crypto firm in the EU or EEA buys from you. DORA reaches you through the contract terms those customers must impose, and through direct oversight if you are designated a critical provider. Answering yes switches on the DORA monitoring add-on.">
                <TriToggle value={form.ictProviderToFinancialEntities ?? null} onChange={(v) => set("ictProviderToFinancialEntities", v)} />
              </Question>
            )}
            {asksScoped("uaeFinancialFreeZone") && (
              <Question label="Established in a UAE financial free zone" help="The DIFC and ADGM have their own data protection laws, which bind establishments there; a UAE mainland company is under the federal PDPL instead.">
                <select value={form.uaeFinancialFreeZone ?? ""} onChange={(e) => set("uaeFinancialFreeZone", e.target.value || null)} style={selectStyle}>
                  <option value="">— not sure —</option>
                  {UAE_FREE_ZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
                </select>
              </Question>
            )}
            <Question label="Countries of legal establishment" help="Where you have a registered entity, branch or office. Laws require a local representative when you serve a market without being established there (EU/UK Art. 27, China, Thailand, Turkey, Saudi Arabia, Switzerland)." wide>
              <CountryPicker selected={form.establishmentCountries ?? []} locked={siteEstablishments} lockedHint="A site here is marked as a legal establishment" onChange={(v) => set("establishmentCountries", v)} />
            </Question>
            <Question label="Countries whose residents you target or monitor, without an establishment there" help="You offer goods or services to people there (a website in their language or currency, delivery there) or monitor their behaviour (tracking, profiling). Their data protection laws apply to you on that test (GDPR / UK GDPR Art. 3(2), China PIPL Art. 3, Brazil LGPD Art. 3), but not their cyber-security or sector laws. Saving changes which laws apply." wide>
              <CountryPicker selected={form.targetedCountries ?? []} onChange={(v) => set("targetedCountries", v)} />
            </Question>
            <Question label="Operating Locations where you only host or process data" help="A data centre or cloud region where you have no office, entity or staff. Hosting alone brings in neither that country's data protection law nor its cyber-security laws, so these countries' laws are taken off your list. Leave empty if every Operating Location is an establishment." wide>
              <CountryPicker selected={form.hostingOnlyCountries ?? []} onChange={(v) => set("hostingOnlyCountries", v)} />
            </Question>
            {asksScoped("processingRiskToIndividuals") && (
              <Question label="Processing likely to result in a risk to individuals" help="Taking into account its nature, scope, context and purposes. The GDPR's representative (Art. 27(2)) and records (Art. 30(5)) exemptions need a No; monitoring, children's data and significant profiling count as indicators of risk whatever you answer.">
                <TriToggle value={form.processingRiskToIndividuals ?? null} onChange={(v) => set("processingRiskToIndividuals", v)} />
              </Question>
            )}
            {(asksScoped("criminalOffenceScale") || form.criminalOffenceData === true) && (
              <Question label="Scale of criminal offence data" help="Large-scale processing of criminal offence data as a core activity makes a DPO and a DPIA mandatory (GDPR Art. 37(1)(c), 35(3)(b)) — background screening or vetting services, for example.">
                <select value={form.criminalOffenceScale ?? ""} onChange={(e) => set("criminalOffenceScale", e.target.value || null)} style={selectStyle}>
                  <option value="">— not sure —</option>
                  {SPECIAL_CATEGORY_SCALES.map((b) => <option key={b.value} value={b.value}>{b.value === "limited" ? "Limited (e.g. occasional DBS checks on staff)" : b.value === "large" ? "Large scale / core activity (e.g. a vetting or screening service)" : b.label}</option>)}
                </select>
              </Question>
            )}
            {asksScoped("sensitiveDataCoreActivity") && (
              <Question label="Large-scale sensitive processing is a core activity" help="Answer No if the large-scale special-category or criminal offence data is only ancillary — staff health records, payroll, HR. Ancillary processing does not make a DPO mandatory (WP243).">
                <TriToggle value={form.sensitiveDataCoreActivity ?? null} onChange={(v) => set("sensitiveDataCoreActivity", v)} />
              </Question>
            )}
            {asksScoped("profilingSignificantEffects") && (
              <Question label="Profiling or automated decisions with significant effects" help="Systematic and extensive evaluation of people by automated means — credit scoring, automated recruitment screening, insurance pricing — producing legal or similarly significant effects. A DPIA is mandatory for it (Art. 35(3)(a)).">
                <TriToggle value={form.profilingSignificantEffects ?? null} onChange={(v) => set("profilingSignificantEffects", v)} />
              </Question>
            )}
            {asksScoped("publicAreaMonitoring") && (
              <Question label="Large-scale monitoring of publicly accessible areas" help="CCTV or other monitoring of public spaces, shopping centres, stations or streets on a large scale. A DPIA is mandatory for it (Art. 35(3)(c)).">
                <TriToggle value={form.publicAreaMonitoring ?? null} onChange={(v) => set("publicAreaMonitoring", v)} />
              </Question>
            )}
            {asksScoped("onlineServiceForChildren") && (
              <Question label="Online service likely to be accessed by children (UK)" help="An app, website, game or connected toy that under-18s in the UK are likely to use, whether or not it is aimed at them. Decides the Children's Code; holding children's data (a school's records) does not.">
                <TriToggle value={form.onlineServiceForChildren ?? null} onChange={(v) => set("onlineServiceForChildren", v)} />
              </Question>
            )}
            {asksScoped("balanceSheetAmount") && (
              <Question label="Annual balance-sheet total" help="In the turnover currency. Under the EU SME definition an enterprise stays within a size class when either its turnover or its balance sheet is within the ceiling, so NIS2, DORA, the UK NIS small-business exclusion and the Turkish VERBİS test read it.">
                <input
                  type="number" min={0} step={1000} value={form.balanceSheetAmount ?? ""} placeholder="e.g. 4000000"
                  onChange={(e) => set("balanceSheetAmount", e.target.value === "" ? null : Number(e.target.value))}
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                />
              </Question>
            )}
            {asksScoped("partOfGroup") && (
              <Question label="Part of a group" help="Owned 25% or more by another enterprise, or owning 25% or more of one. Linked enterprises count together, and partners pro rata, for the NIS2, DORA and UK NIS size tests — a small subsidiary of a large group is itself large. Unanswered, no size exemption is given.">
                <TriToggle value={form.partOfGroup ?? null} onChange={(v) => set("partOfGroup", v)} />
              </Question>
            )}
            {form.partOfGroup === true && (
              <Question label="Group figures (linked enterprises in full, partners pro rata)" help="Headcount, turnover and balance-sheet total in the turnover currency. Not needed when your consultancy has recorded the group on the platform." wide>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {(["groupHeadcount", "groupTurnoverAmount", "groupBalanceSheetAmount"] as const).map((k) => (
                    <input
                      key={k} type="number" min={0} value={form[k] ?? ""} placeholder={k === "groupHeadcount" ? "Headcount" : k === "groupTurnoverAmount" ? "Turnover" : "Balance sheet"}
                      onChange={(e) => set(k, e.target.value === "" ? null : Number(e.target.value))}
                      style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                    />
                  ))}
                </div>
              </Question>
            )}
            {asksScoped("nydfsLicensee") && (
              <Question label="Licensed, registered or chartered by the New York DFS" help="Only an entity operating under a licence, registration or charter under New York's Banking, Insurance or Financial Services Laws is covered by 23 NYCRR 500 and its 72-hour notice.">
                <TriToggle value={form.nydfsLicensee ?? null} onChange={(v) => set("nydfsLicensee", v)} />
              </Question>
            )}
            {asksScoped("dataBroker") && (
              <Question label="Sell data about people you have no direct relationship with" help="A data broker collects and sells or licenses personal data about consumers it does not deal with directly. Selling your own customers' data does not make you one.">
                <TriToggle value={form.dataBroker ?? null} onChange={(v) => set("dataBroker", v)} />
              </Question>
            )}
            {asksScoped("federalInformationSystems") && (
              <Question label="US federal agency, or run an information system for one" help="FISMA binds federal agencies, and contractors only for the information systems they operate on an agency's behalf.">
                <TriToggle value={form.federalInformationSystems ?? null} onChange={(v) => set("federalInformationSystems", v)} />
              </Question>
            )}
            {asksScoped("auPrivacyActCarveOut") && (
              <Question label="Australia: covered whatever your turnover" help="Yes if you are an AML/CTF reporting entity (including lawyers, accountants, real estate agents and trust and company service providers from 1 July 2026), a credit reporting body, a residential tenancy database operator, CDR-accredited, a Commonwealth contracted service provider, related to a covered business, had turnover above A$3m in any year since 2002, or have opted in. Any of these removes the small business exemption." wide>
                <TriToggle value={form.auPrivacyActCarveOut ?? null} onChange={(v) => set("auPrivacyActCarveOut", v)} />
              </Question>
            )}
            </>)}

            {/* Designation thresholds. Only shown when a regime in scope could designate this
                organisation — the engine reports which figures it is actually asking for, so a
                dental practice is never asked for annual litres of fuel. */}
            {asksOesFigures && (
              <>
                <Question label="Operator-of-essential-services thresholds" help="Most cyber-security laws deem an operator designated the moment it crosses a Schedule threshold, whether or not the authority has written to it — the Cyber Security (Jersey) Law says so expressly (Art. 22(2)). These are the figures those Schedules are written in." wide>
                  <div style={{ fontSize: 12, color: theme.color.faint }}>
                    Answer the ones that describe your service. Anything left blank stays an open question rather than an exemption.
                  </div>
                </Question>

                {asks("essentialServiceCustomers") && (
                  <Question label="Customers served by your essential service" help="Metered supplies, connections or accounts. The Cyber Security (Jersey) Law designates an electricity or water supplier at 10,000.">
                    <input
                      type="number" min={0} step={100} value={form.essentialServiceCustomers ?? ""} placeholder="e.g. 24000"
                      onChange={(e) => set("essentialServiceCustomers", e.target.value === "" ? null : Number(e.target.value))}
                      style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                    />
                  </Question>
                )}

                {asks("populationServed") && (
                  <Question label="Population your service covers" help="People the service reaches, where a Schedule counts population rather than customers — the UK NIS Regulations designate a drinking-water supplier at 200,000.">
                    <input
                      type="number" min={0} step={1000} value={form.populationServed ?? ""} placeholder="e.g. 210000"
                      onChange={(e) => set("populationServed", e.target.value === "" ? null : Number(e.target.value))}
                      style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                    />
                  </Question>
                )}

                {asks("essentialServiceVolumeAnnual") && (
                  <Question label="Annual volume of your essential service" help="Fuel supplied, water delivered, passengers carried or freight handled in a year. The unit matters: a figure recorded in the wrong unit is treated as unanswered rather than compared." wide>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="number" min={0} step={1000} value={form.essentialServiceVolumeAnnual ?? ""} placeholder="e.g. 5200000"
                        onChange={(e) => set("essentialServiceVolumeAnnual", e.target.value === "" ? null : Number(e.target.value))}
                        style={{ ...inputStyle, flex: 1, boxSizing: "border-box" }}
                      />
                      <select
                        value={form.essentialServiceVolumeUnit ?? ""}
                        onChange={(e) => set("essentialServiceVolumeUnit", e.target.value || null)}
                        style={{ ...inputStyle, width: 200 }}
                      >
                        <option value="">— unit —</option>
                        {VOLUME_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                  </Question>
                )}

                {asks("regulatoryLicences") && licenceOptions.length > 1 && (
                  <Question label="Licences and designations you hold" help="Several Schedules designate on a licence or registration alone — a Jersey Class II or III telecoms licence, registration under the Banking Business (Jersey) Law, or a designation notice from the authority." wide>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
                      {licenceOptions.map((l) => {
                        const held = (form.regulatoryLicences ?? []).includes(l.value);
                        return (
                          <label key={l.value} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, color: theme.color.text, cursor: "pointer" }}>
                            <input
                              type="checkbox" checked={held}
                              onChange={() => {
                                const current = form.regulatoryLicences ?? [];
                                if (held) return set("regulatoryLicences", current.filter((x) => x !== l.value));
                                // "None of these" and a named licence contradict each other, so
                                // ticking either clears the other rather than storing both.
                                const kept = l.value === LICENCE_NONE ? [] : current.filter((x) => x !== LICENCE_NONE);
                                set("regulatoryLicences", [...kept, l.value]);
                              }}
                              style={{ marginTop: 2 }}
                            />
                            <span>{l.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Question>
                )}
              </>
            )}
          </div>

          </fieldset>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
            {canEdit && (
              <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Save and re-tune obligations"}
              </button>
            )}
            {saved && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved — obligations updated.</span>}
            {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
            <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>Thresholds last reviewed {thresholds.lastReviewed}</span>
          </div>
          {thresholds.regulationsWithoutRules.length > 0 && (
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 10, lineHeight: 1.5 }}>
              No size or volume thresholds are modelled for: {thresholds.regulationsWithoutRules.join(", ")}. Their obligations apply as listed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
